/// <amd-module name="Artifacts/FeedSettings/hooks/AddEditViewForm" />
import { validateViewName } from "Artifacts/Root/Validator";
import { FeedPermission, FeedView, FeedVisibility } from "Artifacts/Root/web-apis/contracts/Feed";
import { useCallback, useReducer, useState } from "react";
import { IIdentity } from "VSSUI/IdentityPicker";
import { IFeedSettings } from "../types";

export interface AddEditViewFormHook {
  isFormDirty: boolean;
  isFormValid: boolean;
  formState: AddEditViewFormState;
  onNameChange: (value: string) => void;
  onFeedVisibilityChange: (value: FeedVisibility) => void;
  onIsDefaultChange: (value: boolean) => void;
  onIdentityAdded: (identity: IIdentity) => void;
  onIdentityRemoved: (identity: IIdentity) => void;
  onIdentitiesRemoved: (identities: IIdentity[]) => void;
  setForm: (value: AddEditViewFormState) => void;
}

export interface AddEditViewFormState {
  viewName: string;
  feedVisibility: FeedVisibility;
  isDefaultView: boolean;
  identities: IIdentity[];
}

export function useAddEditViewForm(
  feedSettings: IFeedSettings,
  editView?: FeedView,
  initialViewPermissions?: FeedPermission[],
): AddEditViewFormHook {
  const [isFormDirty, setFormDirty] = useState<boolean>(false);
  const [formState, dispatch] = useReducer(
    addEditViewFormReducer,
    getDefaultFormState(feedSettings, editView, initialViewPermissions),
  );

  const dispatchAndMakeDirty = (action: AddEditViewFormAction) => {
    setFormDirty(true);
    dispatch(action);
  };

  const onNameChange = (value: string) => dispatchAndMakeDirty({ type: "viewName", value });
  const onFeedVisibilityChange = (value: FeedVisibility) =>
    dispatchAndMakeDirty({ type: "feedVisibility", value });
  const onIsDefaultChange = (value: boolean) =>
    dispatchAndMakeDirty({ type: "isDefaultView", value });
  const onIdentityAdded = (identity: IIdentity) =>
    dispatchAndMakeDirty({ type: "addIdentity", value: identity });
  const onIdentityRemoved = (identity: IIdentity) =>
    dispatchAndMakeDirty({ type: "removeIdentity", value: identity });
  const onIdentitiesRemoved = (identities: IIdentity[]) =>
    dispatchAndMakeDirty({ type: "removeIdentities", value: identities });
  const setForm = useCallback(
    (value: AddEditViewFormState) => dispatch({ type: "form", value }),
    [],
  );

  const isViewNameValid = validateViewName(formState.viewName) || formState.viewName === "";
  const isFormValid = isViewNameValid;

  return {
    formState,
    setForm,
    isFormValid,
    isFormDirty,
    onNameChange,
    onFeedVisibilityChange,
    onIsDefaultChange,
    onIdentitiesRemoved,
    onIdentityAdded,
    onIdentityRemoved,
  };
}

interface AddEditViewFormAction {
  type:
    | "viewName"
    | "feedVisibility"
    | "isDefaultView"
    | "form"
    | "addIdentity"
    | "removeIdentity"
    | "removeIdentities";
  value: boolean | string | FeedVisibility | AddEditViewFormState | IIdentity | IIdentity[];
}

function addEditViewFormReducer(
  state: AddEditViewFormState,
  action: AddEditViewFormAction,
): AddEditViewFormState {
  switch (action.type) {
    case "viewName":
      return { ...state, viewName: action.value as string };
    case "feedVisibility":
      return { ...state, feedVisibility: action.value as FeedVisibility };
    case "isDefaultView":
      return { ...state, isDefaultView: action.value as boolean };
    case "form":
      return { ...state, ...(action.value as AddEditViewFormState) };
    case "addIdentity":
      return { ...state, identities: [...state.identities, action.value as IIdentity] };
    case "removeIdentity":
      return {
        ...state,
        identities: state.identities.filter((identity) => identity !== (action.value as IIdentity)),
      };
    case "removeIdentities":
      return {
        ...state,
        identities: state.identities.filter(
          (identity) => (action.value as IIdentity[]).findIndex((i) => i === identity) === -1,
        ),
      };
  }
}

function getDefaultFormState(
  feedSettings: IFeedSettings,
  editView?: FeedView,
  initialViewPermissions?: FeedPermission[],
): AddEditViewFormState {
  return {
    viewName: editView ? editView.name : "",
    feedVisibility: editView ? editView.visibility : FeedVisibility.Private,
    isDefaultView: editView ? feedSettings.defaultViewId === editView.id : false,
    identities: initialViewPermissions ? feedPermissionToIIdentity(initialViewPermissions) : [],
  };
}

function feedPermissionToIIdentity(feedPermissions: FeedPermission[]): IIdentity[] {
  const identities = feedPermissions.map((permission) => {
    return {
      displayName: permission.displayName,
      localId: permission.identityId,
    } as IIdentity;
  }) as IIdentity[];

  return identities;
}
